import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { groupCustomProperties } from "../utils/customPropertyGroups";
import { CustomProperty } from "@/models/CustomProperty";

type Props = {
  customProperties: CustomProperty[];
  onChange: (next: CustomProperty[]) => void;
};

export default function AssetCustomFields({ customProperties, onChange }: Props) {
  const { t } = useTranslation();
  const groupedEntries = useMemo(() => groupCustomProperties(customProperties, t("assets.otherGroup")), [customProperties, t]);

  const setValue = (index: number, value: string) =>
    onChange(customProperties.map((item, itemIndex) => (itemIndex === index ? { ...item, value } : item)));

  if (customProperties.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("assets.noCustomFields")}
      </Typography>
    );
  }

  return (
    <>
      {groupedEntries.map(([groupName, groupCustomProperties]) => (
        <Stack key={groupName} spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {groupName}
          </Typography>
          {groupCustomProperties.map(customProperty => {
            const index = customProperties.findIndex(
              item => item.property?.id === customProperty.property?.id,
            );
            if (index === -1) return null;

            if (customProperty.property?.propertyType === "Boolean") {
              const checked = String(customProperty.value).toLowerCase() === "true";
              return (
                <FormControlLabel
                  key={customProperty.property?.id ?? `${customProperty.property?.name}-${index}`}
                  control={
                    <Switch
                      checked={checked}
                      onChange={(_, isChecked) => setValue(index, isChecked ? "true" : "false")}
                    />
                  }
                  label={`${customProperty.property?.name ?? `${t("assets.customFieldsTitle")} ${index + 1}`} (${checked ? t("assets.on") : t("assets.off")})`}
                />
              );
            }

            return (
              <TextField
                key={customProperty.property?.id ?? `${customProperty.property?.name}-${index}`}
                label={customProperty.property?.name ?? `${t("assets.customFieldsTitle")} ${index + 1}`}
                size="small"
                value={customProperty.value ?? ""}
                onChange={event => setValue(index, event.target.value)}
              />
            );
          })}
        </Stack>
      ))}
    </>
  );
}
